import { useState, useCallback, useMemo } from 'react'

/**
 * @typedef {Object} RelationshipsMapApi
 * @property {Map<string, Object>} relationships - Map of relationshipId -> relationship object
 * @property {(personId1: string, personId2: string) => Object|null} findRelationship - Find relationship between two persons (order agnostic)
 * @property {(relationship: Object) => void} addOrUpdateRelationship - Add or update a relationship
 * @property {(relationshipId: string) => boolean} removeRelationship - Remove a relationship by ID
 * @property {(relationshipId: string) => Object|undefined} getRelationship - Get a relationship by ID
 * @property {() => Object[]} getAllRelationships - Get all relationships as array
 * @property {(personId: string) => Object[]} getRelationshipsForPerson - Get all relationships for a specific person
 * @property {() => void} clearRelationships - Clear all relationships
 */

/**
 * useRelationshipsMap
 *
 * Custom hook for managing bidirectional relationships between persons in a form.
 * Stores relationships by their ID and provides helper functions to find, add, update,
 * and remove relationships without caring about person order.
 *
 * @param {Array} initialRelationships - Optional initial relationships array
 * @returns {RelationshipsMapApi} Relationship management utilities
 */
const useRelationshipsMap = (initialRelationships = []) => {
  const initializeMap = useCallback((relationshipsArray) => {
    const newMap = new Map()
    relationshipsArray.forEach((rel) => {
      if (rel?.id) {
        newMap.set(rel.id, rel)
      }
    })
    return newMap
  }, [])

  const [relationships, setRelationships] = useState(() => initializeMap(initialRelationships))

  /**
   * Find a relationship between two persons (order doesn't matter)
   * @param {string} personId1 - First person ID
   * @param {string} personId2 - Second person ID
   * @returns {Object|null} The relationship object or null if not found
   */
  const findRelationship = useCallback(
    (personId1, personId2) => {
      if (!personId1 || !personId2) return null

      return (
        Array.from(relationships.values()).find(
          (rel) =>
            (rel.firstPersonId === personId1 && rel.secondPersonId === personId2) ||
            (rel.firstPersonId === personId2 && rel.secondPersonId === personId1)
        ) || null
      )
    },
    [relationships]
  )

  /**
   * Add or update a relationship
   * @param {Object} relationship - Relationship object (must have an id)
   */
  const addOrUpdateRelationship = useCallback((relationship) => {
    if (!relationship?.id) {
      throw new Error('Relationship must have an id')
    }

    setRelationships((prev) => {
      const next = new Map(prev)
      next.set(relationship.id, relationship)
      return next
    })
  }, [])

  /**
   * Remove a relationship by ID
   * @param {string} relationshipId - ID of the relationship to remove
   * @returns {boolean} True if relationship was removed
   */
  const removeRelationship = useCallback((relationshipId) => {
    if (!relationshipId) return false

    setRelationships((prev) => {
      const next = new Map(prev)
      const removed = next.delete(relationshipId)
      return removed ? next : prev
    })

    return true
  }, [])

  /**
   * Get a relationship by ID
   * @param {string} relationshipId - ID of the relationship
   * @returns {Object|undefined} The relationship object or undefined
   */
  const getRelationship = useCallback(
    (relationshipId) => {
      return relationships.get(relationshipId)
    },
    [relationships]
  )

  /**
   * Get all relationships as an array
   * @returns {Array} Array of all relationship objects
   */
  const getAllRelationships = useCallback(() => {
    return Array.from(relationships.values())
  }, [relationships])

  /**
   * Get all relationships for a specific person
   * @param {string} personId - ID of the person
   * @returns {Array} Array of relationship objects where person is involved
   */
  const getRelationshipsForPerson = useCallback(
    (personId) => {
      if (!personId) return []

      return Array.from(relationships.values()).filter(
        (rel) => rel.firstPersonId === personId || rel.secondPersonId === personId
      )
    },
    [relationships]
  )

  /**
   * Clear all relationships
   */
  const clearRelationships = useCallback(() => {
    setRelationships(new Map())
  }, [])

  return useMemo(
    () => ({
      relationships,
      findRelationship,
      addOrUpdateRelationship,
      removeRelationship,
      getRelationship,
      getAllRelationships,
      getRelationshipsForPerson,
      clearRelationships,
    }),
    [
      relationships,
      findRelationship,
      addOrUpdateRelationship,
      removeRelationship,
      getRelationship,
      getAllRelationships,
      getRelationshipsForPerson,
      clearRelationships,
    ]
  )
}

export default useRelationshipsMap
